/**
 * @fileoverview Batch Operations Panel Component
 * 
 * Panel for managing batch assignment operations across multiple files and parrots.
 * Provides selection controls, batch operation buttons, and conflict detection.
 * 
 * Requirements: 4.3, 4.4, 4.5
 */

import React, { useState, useMemo } from 'react';
import { AssignmentPreviewDialog } from './AssignmentPreviewDialog.jsx';

/**
 * Batch Operations Panel Component
 * 
 * @param {Object} props - Component props
 * @param {Array} props.files - Available files
 * @param {Array} props.parrots - Available parrots
 * @param {Set} props.selectedFiles - Selected file IDs
 * @param {Set} props.selectedParrots - Selected parrot IDs
 * @param {Object} props.currentAssignments - Current assignments (fileId -> assignments[])
 * @param {function} props.onBatchAssignment - Batch assignment handler
 * @param {function} props.onFileSelection - File selection handler
 * @param {function} props.onParrotSelection - Parrot selection handler
 * @param {function} props.onConflictCheck - Conflict check handler
 * @param {boolean} [props.showPreview=true] - Whether to show preview dialog
 * @param {string} [props.className] - CSS class name
 */
export const BatchOperationsPanel = ({
  files,
  parrots,
  selectedFiles,
  selectedParrots,
  currentAssignments = {},
  onBatchAssignment,
  onFileSelection,
  onParrotSelection,
  onConflictCheck,
  showPreview = true,
  className=''
}) => {
  // State for preview dialog
  const [previewDialog, setPreviewDialog] = useState({
    isOpen: false,
    operation: null,
    loading: false,
    conflicts: []
  });
  // Handle select all files
  const handleSelectAllFiles = (selected) => {
    files.forEach(file => {
      onFileSelection(file.id, selected);
    });
  };

  // Handle select all parrots
  const handleSelectAllParrots = (selected) => {
    parrots.forEach(parrot => {
      onParrotSelection(parrot.id, selected);
    });
  };

  // Check if all files are selected
  const allFilesSelected = files.length > 0 && selectedFiles.size === files.length;

  // Check if all parrots are selected
  const allParrotsSelected = parrots.length > 0 && selectedParrots.size === parrots.length;

  // Check if batch operations are possible
  const canPerformBatchOps = selectedFiles.size > 0 && selectedParrots.size > 0;

  // Get selected files and parrots as arrays
  const selectedFilesArray = useMemo(() => {
    return files.filter(file => selectedFiles.has(file.id));
  }, [files, selectedFiles]);

  const selectedParrotsArray = useMemo(() => {
    return parrots.filter(parrot => selectedParrots.has(parrot.id));
  }, [parrots, selectedParrots]);

  // Handle batch operation with preview
  const handleBatchOperation = async (operation) => {
    if (!canPerformBatchOps) return;

    if (showPreview) {
      // Show preview dialog
      setPreviewDialog({
        isOpen: true,
        operation,
        loading: true,
        conflicts: []
      });

      try {
        // Check for conflicts
        const conflicts = onConflictCheck ? await onConflictCheck(operation, selectedFilesArray, selectedParrotsArray) : [];
        
        setPreviewDialog(prev => ({
          ...prev,
          loading: false,
          conflicts
        }));
      } catch (error) {
        console.error('Error checking conflicts:', error);
        setPreviewDialog(prev => ({
          ...prev,
          loading: false,
          conflicts: []
        }));
      }
    } else {
      // Execute directly without preview
      onBatchAssignment(operation, Array.from(selectedFiles), Array.from(selectedParrots));
    }
  };

  // Handle preview confirmation
  const handlePreviewConfirm = () => {
    const { operation } = previewDialog;
    setPreviewDialog({ isOpen: false, operation: null, loading: false, conflicts: [] });
    onBatchAssignment(operation, Array.from(selectedFiles), Array.from(selectedParrots));
  };

  // Handle preview cancel
  const handlePreviewCancel = () => {
    setPreviewDialog({ isOpen: false, operation: null, loading: false, conflicts: [] });
  };

  return (
    <div className={`bg-white dark:bg-gray-800 rounded-lg border border-gray-200 dark:border-gray-700 p-6 ${className}`}>
      <div className="flex items-center justify-between mb-4">
        <h3 className="text-lg font-semibold text-gray-900 dark:text-white">
          Batch Operations
        </h3>
        <div className="text-sm text-gray-600 dark:text-gray-400 dark:text-gray-300 dark:text-gray-600">
          {selectedFiles.size} file{selectedFiles.size !== 1 ? 's' : ''} × {selectedParrots.size} parrot{selectedParrots.size !== 1 ? 's' : ''} selected
        </div>
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
        {/* File selection */}
        <div>
          <div className="flex items-center justify-between mb-3">
            <h4 className="text-sm font-medium text-gray-700 dark:text-gray-300 dark:text-gray-600">
              Select Files ({selectedFiles.size}/{files.length})
            </h4>
            <div className="flex items-center space-x-2">
              <input
                type="checkbox"
                id="select-all-files"
                checked={allFilesSelected}
                onChange={(e) => handleSelectAllFiles(e.target.checked)}
                className="h-4 w-4 rounded border-gray-300 dark:border-gray-600 bg-white dark:bg-gray-700 text-teal-600 focus:ring-teal-500 dark:bg-gray-800"
              />
              <label htmlFor="select-all-files" className="text-sm text-gray-700 dark:text-gray-300 dark:text-gray-600">
                Select All
              </label>
            </div>
          </div>
          
          <div className="max-h-32 overflow-y-auto space-y-2 border border-gray-200 dark:border-gray-600 rounded-md p-2 dark:border-gray-700">
            {files.map(file => (
              <label key={file.id} className="flex items-center space-x-2 cursor-pointer hover:bg-gray-50 dark:hover:bg-gray-700 p-1 rounded dark:bg-gray-900">
                <input
                  type="checkbox"
                  checked={selectedFiles.has(file.id)}
                  onChange={(e) => onFileSelection(file.id, e.target.checked)}
                  className="h-4 w-4 rounded border-gray-300 dark:border-gray-600 bg-white dark:bg-gray-700 text-teal-600 focus:ring-teal-500 dark:bg-gray-800"
                />
                <span className="text-sm text-gray-900 dark:text-white truncate">
                  {file.name || 'Unnamed File'}
                </span>
              </label>
            ))}
          </div>
        </div>

        {/* Parrot selection */}
        <div>
          <div className="flex items-center justify-between mb-3">
            <h4 className="text-sm font-medium text-gray-700 dark:text-gray-300 dark:text-gray-600">
              Select Parrots ({selectedParrots.size}/{parrots.length})
            </h4>
            <div className="flex items-center space-x-2">
              <input
                type="checkbox"
                id="select-all-parrots"
                checked={allParrotsSelected}
                onChange={(e) => handleSelectAllParrots(e.target.checked)}
                className="h-4 w-4 rounded border-gray-300 dark:border-gray-600 bg-white dark:bg-gray-700 text-teal-600 focus:ring-teal-500 dark:bg-gray-800"
              />
              <label htmlFor="select-all-parrots" className="text-sm text-gray-700 dark:text-gray-300 dark:text-gray-600">
                Select All
              </label>
            </div>
          </div>
          
          <div className="max-h-32 overflow-y-auto space-y-2 border border-gray-200 dark:border-gray-600 rounded-md p-2 dark:border-gray-700">
            {parrots.map(parrot => (
              <label key={parrot.id} className="flex items-center space-x-2 cursor-pointer hover:bg-gray-50 dark:hover:bg-gray-700 p-1 rounded dark:bg-gray-900">
                <input
                  type="checkbox"
                  checked={selectedParrots.has(parrot.id)}
                  onChange={(e) => onParrotSelection(parrot.id, e.target.checked)}
                  className="h-4 w-4 rounded border-gray-300 dark:border-gray-600 bg-white dark:bg-gray-700 text-teal-600 focus:ring-teal-500 dark:bg-gray-800"
                />
                <span className="text-sm text-gray-900 dark:text-white truncate">
                  {parrot.name}
                </span>
              </label>
            ))}
          </div>
        </div>
      </div>

      {/* Batch operation buttons */}
      <div className="mt-6 pt-4 border-t border-gray-200 dark:border-gray-700">
        <div className="flex items-center justify-between">
          <div className="text-sm text-gray-600 dark:text-gray-400 dark:text-gray-300 dark:text-gray-600">
            {canPerformBatchOps ? (
              <>Ready to perform batch operations on {selectedFiles.size * selectedParrots.size} assignment{selectedFiles.size * selectedParrots.size !== 1 ? 's' : ''}</>
            ) : (
              'Select files and parrots to enable batch operations'
            )}
          </div>
          
          <div className="flex items-center space-x-2">
            <button
              onClick={() => handleBatchOperation('assign')}
              disabled={!canPerformBatchOps}
              className="px-4 py-2 text-sm bg-teal-600 text-white rounded-lg hover:bg-teal-700 disabled:opacity-50 disabled:cursor-not-allowed transition-colors dark:text-white"
              title="Assign selected files to selected parrots"
            >
              Batch Assign
            </button>
            
            <button
              onClick={() => handleBatchOperation('unassign')}
              disabled={!canPerformBatchOps}
              className="px-4 py-2 text-sm bg-red-600 text-white rounded-lg hover:bg-red-700 disabled:opacity-50 disabled:cursor-not-allowed transition-colors dark:text-white"
              title="Remove selected files from selected parrots"
            >
              Batch Remove
            </button>
            
            <button
              onClick={() => handleBatchOperation('reassign')}
              disabled={!canPerformBatchOps}
              className="px-4 py-2 text-sm bg-blue-600 text-white rounded-lg hover:bg-blue-700 disabled:opacity-50 disabled:cursor-not-allowed transition-colors dark:text-white"
              title="Reassign selected files to selected parrots (removes from others)"
            >
              Batch Reassign
            </button>
          </div>
        </div>
      </div>

      {/* Operation preview */}
      {canPerformBatchOps && (
        <div className="mt-4 p-3 bg-blue-50 dark:bg-blue-900/20 border border-blue-200 dark:border-blue-800 rounded-lg">
          <div className="flex items-start space-x-2">
            <svg className="h-5 w-5 text-blue-600 dark:text-blue-400 mt-0.5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M13 16h-1v-4h-1m1-4h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z" />
            </svg>
            <div className="text-sm text-blue-800 dark:text-blue-200">
              <div className="font-medium mb-1">Batch Operation Preview:</div>
              <div className="space-y-1">
                <div>Files: {selectedFilesArray.map(file => file.name).join(', ')}</div>
                <div>Parrots: {selectedParrotsArray.map(parrot => parrot.name).join(', ')}</div>
                <div className="text-xs text-blue-600 dark:text-blue-400 mt-2">
                  {showPreview ? 'Operations will show a preview dialog before execution' : 'Operations will execute immediately'}
                </div>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Assignment Preview Dialog */}
      <AssignmentPreviewDialog
        isOpen={previewDialog.isOpen}
        operation={previewDialog.operation}
        files={selectedFilesArray}
        parrots={selectedParrotsArray}
        currentAssignments={currentAssignments}
        conflicts={previewDialog.conflicts}
        loading={previewDialog.loading}
        onConfirm={handlePreviewConfirm}
        onCancel={handlePreviewCancel}
      />
    </div>
  );
};
